/**
 * A RETÍCULA DE UM QUADRO — a metade do `T2.108` que faltava.
 *
 * Ele, e é o **único** dos 109 pontos em que usa a palavra *essencial*:
 *
 * > *"tem que ter uma forma de aplicar retícula em TODAS AS PÁGINAS, se quiser, para tentar criar
 * > uma UNIFORMIDADE dos desenhos, do traço, etc. Acho que uma feature para aplicação de retícula
 * > EM QUALQUER UM DOS CAMPOS que a gente vai ter é ESSENCIAL para a ferramenta."*
 *
 * A metade de "todas as páginas" já existia (`Pagina.reticula`, desenhada pelos três motores).
 * Esta é a de "qualquer um dos campos": a trama dentro de **um quadro**, no editor.
 *
 * ── POR QUE UM SVG EM `data:` E NÃO UM `<canvas>` ────────────────────────────────────────────
 *
 * O Konva pinta padrão com `fillPatternImage`, que aceita uma `Image` — e a forma óbvia de
 * fabricar o ladrilho seria desenhar num `<canvas>` e passar adiante.
 *
 * Só que aí a fabricação do ladrilho **não se testa**: o `jsdom` dos testes não implementa
 * `getContext("2d")`, e o que sobraria seria conferir no navegador, à mão, toda vez.
 *
 * Como **texto**, a mesma coisa é uma função pura: entra a intensidade e saem as cores, sai uma
 * string. A string é um SVG, o SVG vira `Image` por `data:`, e o Konva pinta igual. O que se
 * ganha é poder cobrar por `expect` que a trama forte é mais forte que a leve — que é a única
 * propriedade que alguém pode querer mudar sem perceber que quebrou.
 *
 * ── A BASE VAI DENTRO DO LADRILHO, E ISSO NÃO É DETALHE ──────────────────────────────────────
 *
 * `fillPatternImage` **substitui** o `fill`. Um quadro que tenha cor de preenchimento E retícula
 * precisa das duas no mesmo ladrilho, senão ligar a trama apagaria a cor. Por isso a cor de fundo
 * é argumento daqui, e não algo que o Konva compõe por cima.
 *
 * E `transparent` continua funcionando: o estilo "Só o contorno" do `Inserir Quadro` tem
 * `fillColor: "transparent"`, e um ladrilho de fundo transparente com pontos é exatamente o que
 * ele deve ser.
 */
import { OPACIDADE_RETICULA, Reticula, TRAMA } from "../api/documento.types";

/** A tinta do ponto, quando ninguém disser outra. É o mesmo cinza-tinta do resto do produto. */
export const TINTA_DA_TRAMA = "#1b1b1b";

/**
 * O ladrilho da trama, como SVG.
 *
 * Devolve `null` quando não há trama — e `null` é o sinal de "não pinte padrão nenhum", que é
 * diferente de pintar um padrão invisível: o segundo custa uma textura e um `Image` por quadro.
 *
 * @param reticula     a intensidade escolhida
 * @param corDeFundo   o `fillColor` do quadro, que entra no ladrilho (ver o cabeçalho)
 * @param corDoPonto   a tinta do ponto
 */
export function svgDaTrama(
  reticula: Reticula | undefined,
  corDeFundo: string,
  corDoPonto: string = TINTA_DA_TRAMA
): string | null {
  const opacidade = OPACIDADE_RETICULA[reticula ?? "nenhuma"];
  if (!opacidade) return null;

  const { PASSO, RAIO } = TRAMA;
  // `shape-rendering="geometricPrecision"` porque o ladrilho é minúsculo e se repete: sem ele o
  // navegador arredonda o ponto para o pixel e a trama ganha faixas mais claras e mais escuras.
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${PASSO}" height="${PASSO}" ` +
    `viewBox="0 0 ${PASSO} ${PASSO}" shape-rendering="geometricPrecision">` +
    `<rect width="${PASSO}" height="${PASSO}" fill="${corDeFundo}"/>` +
    `<circle cx="${PASSO / 2}" cy="${PASSO / 2}" r="${RAIO}" fill="${corDoPonto}" ` +
    `fill-opacity="${opacidade}"/>` +
    `</svg>`
  );
}

/**
 * O mesmo ladrilho, pronto para virar `Image`.
 *
 * `encodeURIComponent` e não base64: o SVG tem menos de 300 bytes, e um `data:` legível é um
 * `data:` que alguém consegue depurar olhando.
 */
export function ladrilhoDaTrama(
  reticula: Reticula | undefined,
  corDeFundo: string,
  corDoPonto: string = TINTA_DA_TRAMA
): string | null {
  const svg = svgDaTrama(reticula, corDeFundo, corDoPonto);
  if (!svg) return null;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** A próxima intensidade, para o botão do menu do item ciclar entre as quatro. */
export function proximaReticula(atual: Reticula | undefined): Reticula {
  const ordem: Reticula[] = ["nenhuma", "leve", "media", "forte"];
  const i = ordem.indexOf(atual ?? "nenhuma");
  return ordem[(i + 1) % ordem.length];
}
